import { MultiValue, SingleValue } from "react-select";
import { useMemo } from "react";
import { queryClient } from "@/ajax/queryClient";
import { StyledAsyncSelect } from "./StyledAsyncSelect";
import { useTRPC } from "@/trpc";

interface SpecialtiesSelectProps {
  value?: { name: string; id: string } | { name: string; id: string }[];
  onChange?: (
    value: { name: string; id: string } | { name: string; id: string }[] | null,
  ) => void;
  organizationBusinessUnitId: string;
  placeholder?: string;
  disabled?: boolean;
  useParent?: boolean;
  isMulti?: boolean;
}

export const ProviderSharedSelect = ({
  value,
  onChange,
  organizationBusinessUnitId,
  isMulti = false,
  placeholder = "Select provider",
  disabled = false,
  useParent = false,
}: SpecialtiesSelectProps) => {
  const trpc = useTRPC();
  const providerShareResult = useMemo(() => {
    return queryClient.fetchQuery(
      trpc.profile.list.queryOptions({
        organizationBusinessUnitId,
        // This select feeds BU actions (job-offer shares, pool assignment),
        // which the API only permits for providers with a live BU
        // affiliation — direct-share-only members would be rejected.
        affiliation: "affiliated",
      }),
    );
  }, []);

  const loadSharedProviders = async (inputValue: string) => {
    const providerOptions = await providerShareResult;
    const lowerInput = inputValue.toLowerCase();
    return providerOptions
      .filter((provider) => provider.isRevealed)
      .map((provider) => ({
        label:
          provider.firstName +
          " " +
          provider.lastName +
          " (" +
          (provider.specialties?.[0]?.name || "No specialty") +
          ")",
        value: provider.userId,
      }))
      .map((option) => ({
        ...option,
        score: option.label.toLowerCase().indexOf(lowerInput),
      }))
      .filter((option) => option.score >= 0)
      .sort((a, b) => {
        // Exact matches first
        if (a.score === 0 && b.score !== 0) {
          return -1;
        }
        if (b.score === 0 && a.score !== 0) {
          return 1;
        }
        // Then by position of match
        return a.score - b.score;
      })
      .map(({ label, value }) => ({ label, value }));
  };

  const handleChange = (
    option:
      | MultiValue<{ label: string; value: string }>
      | SingleValue<{ label: string; value: string }>
      | null,
  ) => {
    if (isMulti) {
      // For multi-select, option is an array of selected options
      const multiOption = option as MultiValue<{
        label: string;
        value: string;
      }>;
      onChange?.(
        multiOption?.map((o) => ({
          name: o.label,
          id: o.value,
        })) || [],
      );
    } else {
      // For single-select, option is a single object
      const singleOption = option as SingleValue<{
        label: string;
        value: string;
      }>;
      onChange?.(
        singleOption
          ? {
              name: singleOption.label,
              id: singleOption.value,
            }
          : null,
      );
    }
  };

  let inputValue = undefined;
  if (value) {
    if (Array.isArray(value)) {
      inputValue = value.map((item) => ({
        label: item.name,
        value: item.id,
      }));
    } else {
      inputValue = {
        label: value.name,
        value: value.id,
      };
    }
  }
  return (
    <StyledAsyncSelect
      value={inputValue}
      loadOptions={loadSharedProviders}
      onChange={handleChange}
      defaultOptions={true}
      isDisabled={disabled}
      placeholder={placeholder}
      isMulti={isMulti}
      isClearable
    />
  );
};
